import React, { useEffect, useState } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { Box, Typography, styled, useMediaQuery, useTheme, Zoom, Divider } from "@mui/material";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import useMealCount from "../hooks/useMealCount";
import { useMyMealsStyles } from "../hooks/useMyMealsStyles";
import MealTabEdit from "./mealTabEdit";
import { MyMealsQuantitySelectionMode } from "../../Account/Subscriptions/subscriptionsUtils/subscriptions-types";
import "../utils/myMealsCSS.css";

const PRODUCTS_PROGRAM_MEAL = "PRODUCTS";

/**
 * useMealTabs -
 * This filters the meals to display, gets the DOM element for each meal and defines the onClick.
 * @param {*} meals
 * @param {*} config
 * @returns
 */
const useMealTabs = (meals, config, recipes, products, currentMeal, selectedOrderRecipeType) => {
  const { getRecipesForMeal, getSelectedRecipeCount } = useMealCount();

  const onClick = (programMealId, offset) => {
    const mealRow = document.getElementById(`ProgramMeal-${programMealId}`);

    window.scroll(0, window.pageYOffset + mealRow.getBoundingClientRect().top - offset);
  };

  const mealsWithSelections = meals.filter((meal) => {
    const recipesForMeal = getRecipesForMeal(recipes, meal, false, null);
    const selectedRecipeCount = getSelectedRecipeCount(recipesForMeal);
    return meal.Quantity > 0 && selectedRecipeCount > 0;
  });

  const mealsWithoutSelections = meals.filter((meal) => {
    const recipesForMeal = getRecipesForMeal(recipes, meal, false, null);
    const selectedRecipeCount = getSelectedRecipeCount(recipesForMeal);
    return meal.Quantity > 0 && selectedRecipeCount === 0;
  });

  const extraMeals = meals.filter((meal) => meal.Quantity === 0);

  const mealsToRender = config.DisplayRecipesByMealType
    ? [...mealsWithSelections, ...mealsWithoutSelections]
    : [...mealsWithSelections, ...mealsWithoutSelections, ...extraMeals];

  let mealSections =
    (mealsToRender && mealsToRender.map((meal) => document.getElementById(`ProgramMeal-${meal.ProgramMealId}`))) || [];
  if (mealsToRender && (products?.length ?? 0) > 1) {
    let productsSection = document.getElementById(`ProgramMeal-${PRODUCTS_PROGRAM_MEAL}`);
    mealSections.push(productsSection);
  }

  const recipesForCurrentMeal = getRecipesForMeal(
    recipes,
    currentMeal,
    config && config.DisplayRecipesByRecipeType,
    selectedOrderRecipeType,
  );

  return {
    onClick,
    meals: mealsToRender,
    mealSections: mealSections,
    recipesForCurrentMeal: recipesForCurrentMeal,
  };
};

/**
 * MealTabs-
 * Displays tabs for different meal types
 * @param {*} props
 * @returns
 */
const MealTabs = (props) => {
  const theme = useTheme();
  const isTabletOrMobile = useMediaQuery("(max-width:824px)", { noSsr: true });

  const mealDaySectionElement = document.getElementById("mealControlBar");
  const mealTabElement = document.getElementById("MealTabs");
  const mealTabEditElement = document.getElementById("mealTabEdit");
  const { classes } = useMyMealsStyles();
  const [activeTab, setActiveTab] = useState(false);
  const [currentMealTab, setCurrentMealTab] = useState(undefined);
  const [intersectionObserver, setIntersectionObserver] = useState(undefined);
  const [tabsHash, setTabsHash] = useState(undefined);

  const { getRecipesForMeal, isMealQuantityFilled, getSelectedRecipeCount } = useMealCount();

  const { meals, mealSections, recipesForCurrentMeal, onClick } = useMealTabs(
    props.day.Meals,
    props.myMealsConfig,
    props.day.Recipes,
    props.day.Products,
    currentMealTab,
    props.selectedOrderRecipeType,
  );

  //Observer logic could be moved into a resuable hook
  //if we need this functionality elsewhere
  const observerCallback = (payload) => {
    const elementsInView = payload.filter((entry) => entry.isIntersecting);

    if (elementsInView.length >= 1) {
      //Only show the first element in viewport to avoid infinte loop
      const currentMealSectionId = elementsInView[0].target.id.replace("ProgramMeal-", "");

      const targetIndex =
        currentMealSectionId == PRODUCTS_PROGRAM_MEAL
          ? meals?.length || -1
          : meals?.findIndex((meal) => meal.ProgramMealId == currentMealSectionId);

      setActiveTab(targetIndex === -1 ? false : targetIndex);
      setCurrentMealTab(targetIndex === -1 || targetIndex >= meals.length ? undefined : meals[targetIndex]);
    }
  };

  // reset IntersectionObserver only if tabs actually change
  const newTabsHash = mealSections && mealSections.reduce((currHash, elem) => currHash + elem?.id + "|", "");
  if (mealSections && mealSections.length > 0 && mealSections[0] && newTabsHash !== tabsHash) {
    setTabsHash(newTabsHash);
    if (intersectionObserver) {
      intersectionObserver.disconnect();
    }

    const observer = new IntersectionObserver(observerCallback, {
      root: null,
      //How many pixels outside of the viewport sets the element as visible
      rootMargin: mealDaySectionElement ? `-${mealDaySectionElement.offsetHeight}px` : "0px",
      //threshold of .25 means a quarter of the element must be in the viewport to count as visible
      threshold: isTabletOrMobile ? 0.2 : 0,
      delay: 100,
    });

    //Observe each Meal to see when it is in the viewport
    mealSections &&
      mealSections.map((section) => {
        section && observer.observe(section);
      });

    setIntersectionObserver(observer);
  }

  useEffect(() => {
    () => {
      intersectionObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", () => {
      //Reset selected meal tab when at the top of the page
      if (window.pageYOffset === 0) {
        //Set to false when no tabs are selected to avoid error for undefined value.
        setActiveTab(false);
        setCurrentMealTab(undefined);
      }
    });

    () => {
      window.removeEventListener("scroll");
    };
  }, []);

  //Desktop just needs to the mealDaySection offset
  let offset = mealDaySectionElement && mealDaySectionElement.offsetHeight;

  //Mobile needs the mealDaysection + mealTabs + mealTabEdit offset
  if (isTabletOrMobile) {
    offset += mealTabEditElement?.offsetHeight + mealTabElement?.offsetHeight;
  }

  let filled = false;

  if (currentMealTab && isMealQuantityFilled(currentMealTab.Quantity, recipesForCurrentMeal && recipesForCurrentMeal)) {
    filled = true;
  }

  const selectedRecipes = getSelectedRecipeCount(recipesForCurrentMeal) || 0;

  const mealTabsElement = document.getElementById("MealTabs");
  const tabEditHeightOffset = mealTabsElement?.offsetHeight + mealDaySectionElement?.offsetHeight;

  const restrictMealQuantitySelection =
    props.myMealsConfig.MealQuantitySelectionMode === MyMealsQuantitySelectionMode.NoChanges;

  return (
    <>
      <Box
        sx={{
          position: "sticky",
          //Offset the top so the tabs sit below the MealDaySection
          top: mealDaySectionElement && mealDaySectionElement.offsetHeight,
          bottom: 0,
          right: 0,
          left: 0,
          overflow: "hidden",
          zIndex: isTabletOrMobile && "999",
          backgroundColor: "#fff",
          display: "flex",
          alignItems: "flex-start",
          flexDirection: "column",
        }}
        id="MealTabs"
      >
        <Tabs
          className={classes.tabs}
          indicatorColor="none"
          orientation={isTabletOrMobile ? "horizontal" : "vertical"}
          scrollButtons
          selectionFollowsFocus={true}
          sx={{ height: "100%", width: "100%" }}
          textColor="primary"
          value={activeTab}
          variant="scrollable"
        >
          {meals &&
            meals.map((meal, index) => {
              const recipesForMeal = getRecipesForMeal(
                props.day.Recipes,
                meal,
                props.myMealsConfig && props.myMealsConfig.DisplayRecipesByRecipeType,
                props.selectedOrderRecipeType,
              );

              return (
                <StyledTab
                  onClick={() => {
                    setActiveTab(index);
                    setCurrentMealTab(meal);
                    onClick(meal.ProgramMealId, offset);
                  }}
                  key={index}
                  value={index}
                  label={<Typography variant="body3">{meal.Name}</Typography>}
                  wrapped
                  icon={
                    isMealQuantityFilled(meal.Quantity, recipesForMeal && recipesForMeal) ? (
                      <CheckCircleIcon color="success" style={{ marginRight: "10px" }} />
                    ) : (
                      <ErrorIcon style={{ marginRight: "10px" }} />
                    )
                  }
                  iconPosition="start"
                />
              );
            })}

          {props.day.Products && (
            <StyledTab
              onClick={() => {
                setActiveTab(meals?.length ?? 0);
                setCurrentMealTab(PRODUCTS_PROGRAM_MEAL);
                onClick(PRODUCTS_PROGRAM_MEAL, offset);
              }}
              key={meals?.length ?? 0}
              value={meals?.length ?? 0}
              label={<Typography variant="body3">{props.myMealsConfig.ProductsHeadingText}</Typography>}
              wrapped
              icon={<ErrorIcon style={{ marginRight: "10px" }} />}
              iconPosition="start"
            />
          )}
        </Tabs>
        {isTabletOrMobile && (
          <Zoom
            // Show divider only when the user has scrolled past the top of the page.
            in={props.scrollPosition > 0}
          >
            <Divider
              flexItem
              sx={{
                // This is used to ensure that the divider extends beyond the main content.
                marginX: `-${theme.spacing(3.5)}`,
                marginY: theme.spacing(0.5),
                borderBottomWidth: "medium",
              }}
            />
          </Zoom>
        )}
      </Box>
      
      {props.myMealsConfig.TabViewForMealTypes && isTabletOrMobile && currentMealTab && (
        <Box
          sx={{
            position: "sticky",
            //Offset the top so the tabs sit below the MealDaySection and MealTabs
            top: tabEditHeightOffset,
            bottom: 0,
            right: 0,
            left: 0,
            overflow: "hidden",
            zIndex: "999",
            backgroundColor: "#fff",
            justifyContent: "flex-start",
            alignItems: "center",
            display: "flex",
          }}
        >
          <MealTabEdit
            filled={filled}
            selectedRecipes={selectedRecipes}
            currentMealTab={currentMealTab}
            day={props.day}
            onClick={props.handleEditMealClick}
            RestrictMealQuantitySelection={restrictMealQuantitySelection}
            RestrictAddRemoveDays={props.myMealsConfig.RestrictAddRemoveDays}
          />
        </Box>
      )}
    </>
  );
};

export default MealTabs;

export const StyledTab = styled(Tab)({
  textTransform: "none",
  paddingLeft: "5px",
  minWidth: "auto",
  textAlign: "left",
  justifyContent: "flex-start",
  flexDirection: "row",
  "@media screen and (max-width: 824px)": {
    justifyContent: "center",
  },
});
